import AppBarSpacer from '../components/common/AppBarSpacer';
import {
  ArrowBack,
  ArrowBackIosNew,
  ArrowForwardIos,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import MiniDrawer from '../components/MiniDrawer';
import CustomerIntakeConversionActions from '../components/CustomerIntake/CustomerIntakeConversionActions';
import CustomerIntakeConversionStatus from '../components/CustomerIntake/CustomerIntakeConversionStatus';
import PageHeader from '../components/common/PageHeader';
import {
  MANAGEMENT_TABLE_HEADER_COLOR,
  ManagementTableActionCell,
  ManagementTableHeaderCell,
  managementTableRowSx,
} from '../components/common/ManagementTable';
import { TableNoData } from '../components/common/TableEmptyState';
import { CustomerIntakeTemplateVersion } from '../types/CustomerIntake.type';
import {
  CustomerIntakeResponseDetail,
  CustomerIntakeResponseListItem,
  getCustomerIntakeErrorMessage,
  getCustomerIntakeResponse,
  getCustomerIntakeTemplate,
  listCustomerIntakeResponses,
} from '../utils/customerIntakeApi';

const formatSubmittedAt = (date: string) =>
  new Date(date).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatValue = (value: string | string[] | boolean | number | null) => {
  if (value === null || value === '') return '—';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value;
};

type CustomerIntakeResponseField =
  CustomerIntakeResponseDetail['sections'][number]['fields'][number];

const renderResponseField = (field: CustomerIntakeResponseField) => (
  <Box
    key={field.fieldId}
    sx={{
      p: 1.5,
      borderRadius: 1,
      backgroundColor: 'action.hover',
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {field.label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {field.displayValue || formatValue(field.value)}
    </Typography>
  </Box>
);

const CustomerIntakeResponses = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const [open, setOpen] = useState(true);
  const [template, setTemplate] =
    useState<CustomerIntakeTemplateVersion | null>(null);
  const [responses, setResponses] = useState<CustomerIntakeResponseListItem[]>(
    [],
  );
  const [selectedResponse, setSelectedResponse] =
    useState<CustomerIntakeResponseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [totalItems, setTotalItems] = useState(0);

  const loadPage = async () => {
    if (!templateId) {
      setError('Customer intake form version was not provided.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [templateResult, responseResult] = await Promise.all([
        getCustomerIntakeTemplate(templateId),
        listCustomerIntakeResponses(templateId),
      ]);
      setTemplate(templateResult);
      setResponses(responseResult.items);
      setTotalItems(responseResult.metadata.totalItems);
      setError('');
    } catch (loadError) {
      setError(
        getCustomerIntakeErrorMessage(
          loadError,
          'Unable to load customer intake responses.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPage();
  }, [templateId]);

  const handleViewResponse = async (
    response: CustomerIntakeResponseListItem,
  ) => {
    if (!templateId) return;
    setLoadingDetail(true);
    try {
      const detail = await getCustomerIntakeResponse(templateId, response.id);
      setSelectedResponse(detail);
      setError('');
    } catch (detailError) {
      setError(
        getCustomerIntakeErrorMessage(
          detailError,
          'Unable to load the submitted information.',
        ),
      );
    } finally {
      setLoadingDetail(false);
    }
  };

  const selectedResponseIndex = selectedResponse
    ? responses.findIndex((response) => response.id === selectedResponse.id)
    : -1;
  const consentFields =
    selectedResponse?.sections.flatMap((section) =>
      section.fields.filter((field) => field.type === 'consent'),
    ) || [];

  const handleNavigateResponse = async (direction: -1 | 1) => {
    const nextIndex = selectedResponseIndex + direction;
    const nextResponse = responses[nextIndex];
    if (!nextResponse) return;

    await handleViewResponse(nextResponse);
  };

  return (
    <div className="flex h-screen">
      <MiniDrawer open={open} setOpen={setOpen} />
      <main className="flex-1 overflow-auto app-page-bg">
        <AppBarSpacer />
        <div className="h-full px-6 py-4">
          <PageHeader
            title="Customer Intake Responses"
            subtitle={
              template
                ? `${template.name} · Version ${template.version}`
                : 'View submitted customer information.'
            }
            action={
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/customer-survey/templates')}
              >
                Back to Form versions
              </Button>
            }
          />

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void loadPage()}
                >
                  Try again
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Paper sx={{ p: 4 }}>
              <Stack spacing={1} alignItems="center">
                <CircularProgress size={28} />
                <Typography color="text.secondary">
                  Loading submitted customers...
                </Typography>
              </Stack>
            </Paper>
          ) : (
            <Paper sx={{ overflow: 'hidden' }}>
              <Box
                p={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
              >
                <Box>
                  <Typography
                    variant="h6"
                    color="secondary.main"
                    fontWeight={700}
                  >
                    Submitted customers
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {totalItems} response{totalItems === 1 ? '' : 's'} submitted
                    for this form version.
                  </Typography>
                </Box>
                {template && (
                  <Chip
                    label={`Version ${template.version}`}
                    color={template.status === 'ACTIVE' ? 'success' : 'default'}
                  />
                )}
              </Box>

              <TableContainer>
                <Table
                  size="small"
                  padding="checkbox"
                  sx={{ minWidth: 900 }}
                  aria-label="Customer intake responses"
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR,
                      }}
                    >
                      <ManagementTableHeaderCell>#</ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        Submitted at
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        Full name
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        Phone
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        Email
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        Conversion
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell sticky>
                        Actions
                      </ManagementTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {responses.map((response, index) => (
                      <TableRow
                        key={response.id}
                        sx={managementTableRowSx(
                          selectedResponse?.id === response.id,
                        )}
                      >
                        <TableCell
                          align="center"
                          sx={{ width: 64, whiteSpace: 'nowrap' }}
                        >
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatSubmittedAt(response.submittedAt)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" fontWeight={600}>
                            {response.customerName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {response.phone || '—'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {response.email || '—'}
                        </TableCell>
                        <TableCell sx={{ minWidth: 270 }}>
                          <CustomerIntakeConversionStatus
                            status={response.conversionStatus}
                          />
                        </TableCell>
                        <ManagementTableActionCell
                          selected={selectedResponse?.id === response.id}
                        >
                          <Tooltip title="View submitted information">
                            <span>
                              <IconButton
                                color="primary"
                                size="small"
                                aria-label={`View response from ${response.customerName || 'customer'}`}
                                onClick={() =>
                                  void handleViewResponse(response)
                                }
                                disabled={loadingDetail}
                              >
                                <VisibilityOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </ManagementTableActionCell>
                      </TableRow>
                    ))}
                    {responses.length === 0 && (
                      <TableNoData message="No submitted responses found" />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </div>
      </main>

      <Dialog
        fullWidth
        maxWidth="md"
        open={Boolean(selectedResponse)}
        onClose={() => setSelectedResponse(null)}
        scroll="paper"
      >
        <DialogTitle>Submitted information</DialogTitle>
        <DialogContent dividers>
          {selectedResponse && (
            <Stack spacing={2}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={0.75}>
                  <Typography variant="h6" color="secondary.main">
                    {selectedResponse.customerName || 'Unnamed customer'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Submitted {formatSubmittedAt(selectedResponse.submittedAt)}
                  </Typography>
                  <Typography variant="body2">
                    {selectedResponse.phone || '—'} ·{' '}
                    {selectedResponse.email || '—'}
                  </Typography>
                </Stack>
              </Card>

              {selectedResponse.sections.map((section) => {
                const regularFields = section.fields.filter(
                  (field) => field.type !== 'consent',
                );

                if (regularFields.length === 0) return null;

                return (
                  <Box key={section.sectionId}>
                    <Typography
                      variant="subtitle1"
                      color="secondary.main"
                      fontWeight={700}
                      sx={{ mb: 1 }}
                    >
                      {section.title}
                    </Typography>
                    <Stack spacing={1}>
                      {regularFields.map(renderResponseField)}
                    </Stack>
                  </Box>
                );
              })}

              {consentFields.length > 0 && (
                <Box
                  sx={{
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack spacing={1}>
                    {consentFields.map(renderResponseField)}
                  </Stack>
                </Box>
              )}

              <CustomerIntakeConversionActions
                response={selectedResponse}
                onConversionCompleted={() => void loadPage()}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              gridColumn: 2,
            }}
          >
            <Tooltip title="View previous response">
              <span>
                <IconButton
                  size="small"
                  aria-label="View previous response"
                  onClick={() => void handleNavigateResponse(-1)}
                  disabled={loadingDetail || selectedResponseIndex <= 0}
                >
                  <ArrowBackIosNew fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {selectedResponseIndex >= 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 96, textAlign: 'center' }}
              >
                Response {selectedResponseIndex + 1} of {responses.length}
              </Typography>
            )}
            <Tooltip title="View next response">
              <span>
                <IconButton
                  size="small"
                  aria-label="View next response"
                  onClick={() => void handleNavigateResponse(1)}
                  disabled={
                    loadingDetail ||
                    selectedResponseIndex < 0 ||
                    selectedResponseIndex >= responses.length - 1
                  }
                >
                  <ArrowForwardIos fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Button
            onClick={() => setSelectedResponse(null)}
            sx={{ gridColumn: 3, justifySelf: 'end' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CustomerIntakeResponses;
